/*
  Insights card — shows AI-generated nudges on the Hub. Auto-loads on mount.
  Each nudge is dismissible. A "Generate now" button calls the backend
  synchronously when the user wants fresh insights (e.g. first visit).
*/
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, X, RefreshCw, ArrowRight, Loader2 } from "lucide-react";
import { phase4Api } from "../lib/phase4Api";

const KIND_META = {
  adherence:    { color: "var(--primary-soft)",  ink: "var(--primary-ink)",  label: "Meds" },
  shopping:     { color: "var(--accent-soft)",   ink: "var(--accent-ink)",   label: "Shopping" },
  todo:         { color: "var(--warning-bg)",    ink: "var(--warning)",      label: "To-Do" },
  routine:      { color: "var(--primary-soft)",  ink: "var(--primary-ink)",  label: "Routines" },
  subscription: { color: "var(--accent-soft)",   ink: "var(--accent-ink)",   label: "Life Admin" },
  general:      { color: "var(--primary-soft)",  ink: "var(--primary-ink)",  label: "Tip" },
};

export default function InsightsCard() {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  async function load() {
    try {
      const rows = await phase4Api.listInsights({ limit: 5 });
      setInsights(rows || []);
    } catch {
      // Feature likely off — stay silent
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function dismiss(id) {
    setInsights((prev) => prev.filter((i) => i.id !== id));
    try {
      await phase4Api.dismissInsight(id);
    } catch {
      toast.error("Couldn't dismiss — try again");
      load();
    }
  }

  async function generate() {
    setGenerating(true);
    try {
      const r = await phase4Api.generateInsights();
      if ((r?.created || 0) > 0) {
        toast.success(`${r.created} new insight${r.created === 1 ? "" : "s"}`);
      } else {
        toast.message("No new insights right now — check back tomorrow.");
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  }

  if (loading) return null;

  // Empty state: show a tiny CTA to generate on first visit
  if (insights.length === 0) {
    return (
      <section
        data-testid="insights-empty"
        className="card-tactile rounded-2xl p-5 flex items-center justify-between gap-4"
      >
        <div className="flex items-start gap-3 min-w-0">
          <div className="h-9 w-9 rounded-lg bg-[color:var(--primary-soft)] flex items-center justify-center shrink-0">
            <Sparkles className="h-4 w-4" style={{ color: "var(--primary-ink)" }} />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-[color:var(--text-main)]">AI Insights</div>
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>
              Generate friendly nudges from your recent activity.
            </p>
          </div>
        </div>
        <button
          onClick={generate}
          disabled={generating}
          data-testid="insights-generate"
          className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-lg text-sm font-semibold border shrink-0"
          style={{ borderColor: "var(--border-subtle)", color: "var(--primary-ink)" }}
        >
          {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {generating ? "Thinking…" : "Generate"}
        </button>
      </section>
    );
  }

  return (
    <section data-testid="insights-card" className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4" style={{ color: "var(--primary-ink)" }} />
          <span className="label-caps">AI Insights</span>
        </div>
        <button
          onClick={generate}
          disabled={generating}
          data-testid="insights-refresh"
          className="text-xs font-semibold inline-flex items-center gap-1 hover:underline disabled:opacity-50"
          style={{ color: "var(--primary-ink)" }}
        >
          {generating ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Refresh
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {insights.slice(0, 3).map((ins) => {
          const meta = KIND_META[ins.kind] || KIND_META.general;
          return (
            <article
              key={ins.id}
              data-testid={`insight-${ins.id}`}
              className="card-tactile rounded-xl p-4 flex flex-col gap-2 relative"
            >
              <button
                onClick={() => dismiss(ins.id)}
                data-testid={`insight-dismiss-${ins.id}`}
                aria-label="Dismiss"
                className="absolute top-2 right-2 h-6 w-6 rounded-md hover:bg-[color:var(--surface-2)] flex items-center justify-center"
                style={{ color: "var(--text-muted)" }}
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <div className="flex items-center gap-2">
                <span
                  className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full"
                  style={{ background: meta.color, color: meta.ink }}
                >
                  {meta.label}
                </span>
              </div>
              <h3 className="font-semibold text-[color:var(--text-main)] text-sm pr-6 leading-snug">
                {ins.title}
              </h3>
              <p className="text-sm text-[color:var(--text-muted)] leading-relaxed">
                {ins.body}
              </p>
              {ins.action_url && ins.action_label && (
                <Link
                  to={ins.action_url}
                  data-testid={`insight-action-${ins.id}`}
                  className="mt-1 inline-flex items-center gap-1 text-xs font-semibold hover:underline"
                  style={{ color: "var(--primary-ink)" }}
                >
                  {ins.action_label} <ArrowRight className="h-3 w-3" />
                </Link>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}
